import { lazy, Suspense, useMemo, type Ref } from 'react';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DotsSixVerticalIcon, MapPinIcon, PlusIcon, WarningOctagonIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { DesktopOnlyHint } from '@/components/handoff/DesktopOnlyHint';
import type { ProjectMapRef } from '@/components/project/project-map';
import { StoryCard } from '@/components/project/story-card';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Segmented } from '@/components/ui/segmented';
import { PageTitle } from '@/components/ui/text';
import type { Story } from '@/types/cmsTypes';
import type { PlanState } from '@/modules/subscriptionPlan/planState';
import { isMobileDevice } from '@/utils/deviceDetection';

// Loaded only once the map view is chosen
const ProjectMap = lazy(() => import('@/components/project/project-map').then(module => ({ default: module.ProjectMap })));

interface ProjectStoriesProps {
  stories: Story[];
  viewMode: 'list' | 'map';
  onViewModeChange: (mode: 'list' | 'map') => void;
  isCreatingStory: boolean;
  onAddStory: () => void;
  onDragEnd: (event: DragEndEvent) => void;
  onEditStory: (story: Story) => void;
  onDeleteStory: (story: Story) => void;
  onSelectStory: (story: Story) => void;
  selectedStoryId?: string;
  plan: PlanState;
  onRefresh?: () => void | Promise<void>;
  /** The stories' statuses could not be read: their cards show none, and a banner offers Try again. */
  statusUnknown?: boolean;
  /** Runs a card's write as a local write, so the page's refresh never undoes it. */
  runWrite?: <T>(write: () => Promise<T>) => Promise<T>;
  /** The stories could not be read: an error with Try again replaces the list. */
  loadError?: boolean;
  onRetry?: () => void;
  mapRef?: Ref<ProjectMapRef>;
  /** The map saves its picture for the project only with all three. */
  onSaveMapImage?: (imageUrl: string | null) => void;
  projectId?: string;
  userId?: string;
}

/** A project page's stories: the heading with the list or map view and New Story, then the stories in order, dragged to reorder. */
export function ProjectStories({
  stories,
  viewMode,
  onViewModeChange,
  isCreatingStory,
  onAddStory,
  onDragEnd,
  onEditStory,
  onDeleteStory,
  onSelectStory,
  selectedStoryId,
  plan,
  onRefresh,
  statusUnknown = false,
  runWrite,
  loadError = false,
  onRetry,
  mapRef,
  onSaveMapImage,
  projectId,
  userId,
}: ProjectStoriesProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Memoized sorted stories to avoid re-sorting on every render
  const sortedStories = useMemo(() => {
    return [...stories].sort((a, b) => a.order - b.order);
  }, [stories]);

  const list = (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={stories.map((story) => story.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-4">
          {sortedStories.map((story) => (
            <StoryCard
              key={story.id}
              story={story}
              onEdit={onEditStory}
              onDelete={onDeleteStory}
              isDraggable={true}
              preCalculatedStatus={story.preCalculatedStatus}
              preCalculatedDuration={story.preCalculatedDuration}
              preCalculatedDraftCount={story.preCalculatedDraftCount}
              preCalculatedDraftSceneInstanceId={story.preCalculatedDraftSceneInstanceId}
              preCalculatedAnchorTypes={story.preCalculatedAnchorTypes}
              preCalculatedUpdatedAt={story.preCalculatedUpdatedAt}
              plan={plan}
              onRefresh={onRefresh}
              statusUnknown={statusUnknown}
              runWrite={runWrite}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );

  return (
    <>
      {/* Stories Header */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <PageTitle as="h2">Stories</PageTitle>
        <div className="flex items-center gap-2 text-lureo-14 text-lureo-ink-subtle leading-5">
          <DotsSixVerticalIcon className="h-5 w-5" />
          <span className="hidden sm:inline">Drag to reorder</span>
          <span className="sm:hidden">Reorder</span>
        </div>
        <Segmented
          variant="pills"
          label="View"
          options={[
            { id: 'list', label: <><span className="hidden lg:inline">List View</span><span className="lg:hidden">List</span></> },
            { id: 'map', label: <><span className="hidden lg:inline">Map View</span><span className="lg:hidden">Map</span></> },
          ]}
          value={viewMode}
          onChange={onViewModeChange}
          className="ml-auto"
        />
        <div className="flex w-full flex-col items-stretch gap-1 sm:w-auto">
          <div className="flex w-full gap-2 sm:w-auto">
            <LureoButton
              data-tour="new-story"
              variant="secondary"
              className="h-12 flex-1 rounded-lureo-radius-button-pill px-5 text-lureo-16 min-h-[44px] sm:min-h-0 sm:flex-none leading-6"
              onClick={onAddStory}
              disabled={isCreatingStory || isMobileDevice()}>
              {isCreatingStory ? (
                <>
                  {/* The call to action's own text colour */}
                  <LoadingSpinner size="sm" tone="on-cta" />
                  Creating...
                </>
              ) : (
                <>
                  <PlusIcon className="h-5 w-5"/>
                  <span className="hidden lg:inline">New Story</span>
                  <span className="lg:hidden">New</span>
                </>
              )}
            </LureoButton>
          </div>
          {isMobileDevice() && <DesktopOnlyHint className="block w-full text-center" />}
        </div>
      </div>

      {statusUnknown && stories.length > 0 && (
        <Card padding="md" className="flex flex-wrap items-center gap-3">
          <WarningOctagonIcon className="h-5 w-5 shrink-0 text-lureo-warning-ink" />
          <p className="flex-1 text-lureo-14 text-lureo-ink leading-5">We couldn't read your stories' status, so it isn't shown.</p>
          <LureoButton variant="outline-2" className="h-9 rounded-lureo-radius-button-pill px-4" onClick={onRetry}>Try again</LureoButton>
        </Card>
      )}

      {/* Stories List with Drag and Drop */}
      {loadError ? (
        <Card padding="lg">
          <EmptyState
            icon={<WarningOctagonIcon />}
            title="We couldn't load the stories"
            action={<LureoButton variant="outline-2" className="h-9 rounded-lureo-radius-button-pill px-4" onClick={onRetry}>Try again</LureoButton>}
          >
            Your stories are safe. Try again in a moment.
          </EmptyState>
        </Card>
      ) : stories.length > 0 ? (
        viewMode === 'map' ? (
          <div className="grid grid-cols-1 gap-6 md:gap-8 xl:grid-cols-2">
            <div>
              <Suspense fallback={<LoadingSpinner size="lg" />}>
                <ProjectMap
                  ref={mapRef}
                  stories={stories}
                  onStorySelect={onSelectStory}
                  selectedStoryId={selectedStoryId}
                  showNoLocationMessage={true}
                  onSaveMapImage={onSaveMapImage}
                  projectId={projectId}
                  userId={userId}
                />
              </Suspense>
            </div>
            <div className="space-y-4">{list}</div>
          </div>
        ) : (
          list
        )
      ) : (
        <Card padding="lg">
          <EmptyState icon={<MapPinIcon />} title="No stories yet">Add a new story to get started!</EmptyState>
        </Card>
      )}
    </>
  );
}
